/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { connect } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { useTranslation } from "react-i18next";
import { Field, reduxForm, formValueSelector } from "redux-form";
import FormClientSelector from "components/FormClientSelector";
import FormSelector from "components/FormSelector";
import FormDateTimePicker from "components/FormDateTimePicker";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import FormSwitch from "components/FormSwitch";

import {
  buildReservationSubmitData,
  getApartments,
  getApartmentsNames,
  getClients,
  getClientsNames,
  isOmnitecConfigured,
  datesIsCorrect,
} from "../../clientReservationsUtils";

const validate = (values) => {
  const errors = {};
  const requiredFields = ["projectId", "apartmentId", "startDate", "endDate", "clientId"];

  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = "Required";
    }
  });

  if (!datesIsCorrect(values)) {
    errors._error = "Invalid date range";
  }

  return errors;
};

function ClientReservationForm(props) {
  const {
    handleSubmit,
    pristine,
    onSubmit,
    actionType,
    projects,
    apartments,
    clients,
    projectId,
    apartmentId,
    change,
    initialValues,
    disableClientSelector,
    disableProjectSelector,
    disableApartmentSelector,
    error,
    showDeleteButton,
    onDelete,
    createOmnitecCodeValue,
    onCreateClient,
  } = props;
  const { t } = useTranslation();

  const [showCreateCode, setShowCreateCode] = useState(false);

  const omnitecAvailable = isOmnitecConfigured(apartments, apartmentId);

  const projectTimeZone =
    projectId && projects?.[projectId]?.timeZone ? projects[projectId].timeZone : "UTC";

  const getApartmentsForProject = () => getApartments(projects, projectId);
  const getApartmentsNamesForProject = () => getApartmentsNames(projects, projectId, apartments);

  const getClientsForProject = () => getClients(clients);
  const getClientsNamesForProject = () => getClientsNames(clients, t);

  const submitData = (data) => {
    onSubmit(
      buildReservationSubmitData(data, {
        projects,
        apartments,
        projectId,
        apartmentId,
      })
    );
  };

  useEffect(() => {
    setShowCreateCode(!!omnitecAvailable);

    const hadLockCode = !!initialValues?.lockData?.keyboardPwd;

    if (actionType === "edit" && hadLockCode) {
      change("createOmnitecCode", true);
      return;
    }

    if (typeof createOmnitecCodeValue === "undefined") {
      change("createOmnitecCode", !!omnitecAvailable);
    }
  }, [omnitecAvailable, createOmnitecCodeValue, change, actionType, initialValues]);

  const translatedReduxFormError = error ? t(error) : "";

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(submitData)}>
        <Grid container justifyContent="center" alignItems="center" sx={{ overflow: "visible" }}>
          <Card id="basic-info" sx={{ overflow: "visible" }}>
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("clientReservation")}
            </MDBox>

            <MDBox pt={3} pb={3} px={3}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="projectId"
                    values={Object.keys(projects || {})}
                    names={Object.values(projects || {})}
                    component={FormSelector}
                    label={t("project")}
                    disabled={actionType === "edit" || disableProjectSelector}
                    onChange={() => change("apartmentId", "")}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="apartmentId"
                    values={getApartmentsForProject()}
                    names={getApartmentsNamesForProject()}
                    component={FormSelector}
                    label={t("apartment")}
                    disabled={actionType === "edit" || disableApartmentSelector || !projectId}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="clientId"
                    values={getClientsForProject()}
                    names={getClientsNamesForProject()}
                    component={FormClientSelector}
                    label={t("client")}
                    disabled={actionType === "edit" || disableClientSelector}
                    onCreateClient={onCreateClient}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="startDate"
                    component={FormDateTimePicker}
                    label={t("startDate")}
                    timeZone={projectTimeZone}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="endDate"
                    component={FormDateTimePicker}
                    label={t("endDate")}
                    timeZone={projectTimeZone}
                  />
                </Grid>

                {showCreateCode && actionType === "add" && (
                  <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      flexDirection="row"
                      mt={2}
                      xs={12}
                    >
                      <MDTypography lineHeight="2.5" color="text" variant="body2">
                        {t("randomEntryCode")}
                      </MDTypography>
                      <Field name="createOmnitecCode" component={FormSwitch} />
                    </MDBox>
                  </Grid>
                )}

                {showCreateCode && actionType === "edit" && initialValues?.lockData?.keyboardPwd && (
                  <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      flexDirection="row"
                      mt={2}
                      xs={12}
                    >
                      <MDTypography lineHeight="2.5" color="text" variant="body2">
                        {`${t("entryCode")}: ${initialValues?.lockData?.keyboardPwd || ""}`}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" alignItems="center" flexDirection="column" mt={2} xs={12}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDButton
                        type="submit"
                        variant="gradient"
                        color="dark"
                        size="small"
                        disabled={pristine}
                      >
                        {t("Save")}
                      </MDButton>

                      {showDeleteButton && (
                        <MDButton variant="gradient" color="error" size="small" onClick={onDelete}>
                          {t("delete")}
                        </MDButton>
                      )}
                    </MDBox>

                    {!!error && (
                      <MDTypography color="error" variant="body2" sx={{ mt: 1 }}>
                        {translatedReduxFormError}
                      </MDTypography>
                    )}
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
        <Field name="createOmnitecCode" component="input" type="hidden" />
      </form>
    </MDBox>
  );
}

ClientReservationForm.defaultProps = {
  projects: {},
  apartments: {},
  clients: {},
  initialValues: {},
  projectId: "",
  apartmentId: "",
  disableClientSelector: false,
  disableProjectSelector: false,
  disableApartmentSelector: false,
  error: "",
  showDeleteButton: false,
  onDelete: null,
  createOmnitecCodeValue: undefined,
  onCreateClient: () => {},
};

ClientReservationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  apartmentId: PropTypes.string,
  projects: PropTypes.object,
  apartments: PropTypes.object,
  clients: PropTypes.object,
  initialValues: PropTypes.object,
  disableClientSelector: PropTypes.bool,
  disableProjectSelector: PropTypes.bool,
  disableApartmentSelector: PropTypes.bool,
  error: PropTypes.any,
  showDeleteButton: PropTypes.bool,
  onDelete: PropTypes.func,
  createOmnitecCodeValue: PropTypes.bool,
  onCreateClient: PropTypes.func,
};

const selector = formValueSelector("ClientReservationForm");

const mapStateToProps = (state, props) => {
  const {
    actionType,
    uids,
    fixedClientId,
    fixedProjectId,
    fixedApartmentId,
    fixedStartDate,
    fixedEndDate,
  } = props;

  const { clientReservationsReducer, clientsReducer, projectsReducer, apartmentsReducer } = state;

  const clientReservationsObj = clientReservationsReducer?.clientReservations;
  const clientReservationsDict = Object.values(clientReservationsObj).flatMap((item) =>
    item && typeof item === "object" ? Object.values(item) : []
  );

  let reservationData = {};

  const getProjectTimeZone = (projId) => {
    if (projId && projectsReducer?.[projId]?.timeZone) {
      return projectsReducer[projId].timeZone;
    }
    return "UTC";
  };

  if (actionType === "edit" && uids?.length === 1) {
    const reservationUid = uids[0];
    const found = (clientReservationsDict || []).find((r) => r?.uid === reservationUid);

    if (found) {
      reservationData = { ...found };
      const timeZone = getProjectTimeZone(reservationData.projectId);

      if (reservationData.startDate) {
        reservationData.startDate = moment.tz(reservationData.startDate, timeZone);
      }
      if (reservationData.endDate) {
        reservationData.endDate = moment.tz(reservationData.endDate, timeZone);
      }
    }
  }

  if (actionType === "add") {
    if (fixedProjectId) reservationData.projectId = fixedProjectId;
    if (fixedApartmentId) reservationData.apartmentId = fixedApartmentId;

    const timeZone = getProjectTimeZone(fixedProjectId);

    if (fixedStartDate) {
      reservationData.startDate = moment.tz(fixedStartDate, timeZone);
    }
    if (fixedEndDate) {
      reservationData.endDate = moment.tz(fixedEndDate, timeZone);
    }
  }

  const defaultTimeZone = getProjectTimeZone(reservationData.projectId || fixedProjectId);

  if (!reservationData.startDate) {
    reservationData.startDate = moment.tz(defaultTimeZone).startOf("day").hour(14).minute(0);
  }

  if (!reservationData.endDate) {
    reservationData.endDate = moment
      .tz(defaultTimeZone)
      .add(1, "days")
      .startOf("day")
      .hour(12)
      .minute(0);
  }

  if (actionType === "add" && fixedClientId) {
    reservationData.clientId = fixedClientId;
  }

  return {
    initialValues: reservationData,
    projects: projectsReducer,
    apartments: apartmentsReducer,
    clients: clientsReducer,
    projectId: selector(state, "projectId"),
    apartmentId: selector(state, "apartmentId"),
    createOmnitecCodeValue: selector(state, "createOmnitecCode"),
    clientReservationsDict,
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: "ClientReservationForm",
    validate,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
  })(ClientReservationForm)
);
